import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { InfoIcon } from 'lucide-react';
export function UserManagement() {
  const [activeTab, setActiveTab] = useState<'list' | 'add'>('list');
  const [currentStep, setCurrentStep] = useState(0);
  // Form State
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    nid: '',
    role: '',
    station: '',
    permissions: {
      approveBeneficiaries: false,
      issuePermits: false,
      manageVessels: false,
      fileCases: false
    }
  });
  const users = [{
    name: 'Rahim Uddin',
    role: 'Range Officer',
    station: 'Chandpai Range',
    status: 'active' as const,
    lastActive: '2024-03-30 10:30'
  }, {
    name: 'Karim Sheikh',
    role: 'Station Officer',
    station: 'Katka Station',
    status: 'active' as const,
    lastActive: '2024-03-30 09:15'
  }, {
    name: 'Fatema Begum',
    role: 'Field Officer',
    station: 'Dhangmari Station',
    status: 'active' as const,
    lastActive: '2024-03-30 08:45'
  }, {
    name: 'Abdul Hakim',
    role: 'HQ Admin',
    station: 'Headquarters',
    status: 'active' as const,
    lastActive: '2024-03-29 16:20'
  }, {
    name: 'Jamal Hossain',
    role: 'Field Officer',
    station: 'Harbaria Station',
    status: 'suspended' as const,
    lastActive: '2024-03-25 14:30'
  }];
  const columns = [{
    key: 'name',
    label: 'Name'
  }, {
    key: 'role',
    label: 'Role'
  }, {
    key: 'station',
    label: 'Station/Range'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-2785" />
  }, {
    key: 'lastActive',
    label: 'Last Active'
  }];
  const filters = [{
    key: 'role',
    label: 'Filter by Role',
    options: ['HQ Admin', 'Range Officer', 'Station Officer', 'Field Officer']
  }, {
    key: 'station',
    label: 'Filter by Station',
    options: ['Chandpai Range', 'Katka Station', 'Dhangmari Station', 'Harbaria Station']
  }, {
    key: 'status',
    label: 'Filter by Status',
    options: ['Active', 'Suspended', 'Inactive']
  }];
  const wizardSteps = [{
    title: 'Basic Info',
    description: 'Enter user basic information'
  }, {
    title: 'Role Assignment',
    description: 'Assign role and permissions'
  }, {
    title: 'Review & Confirm',
    description: 'Review all information before submitting'
  }];
  return <div className="space-y-6" data-id="element-2786">
      <div data-id="element-2787">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2788">User Management</h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-2789">
          Manage system users and their roles
        </p>
      </div>

      {/* Role-based visibility info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-2790">
        <InfoIcon className="text-blue-600 flex-shrink-0 mt-0.5" size={20} data-id="element-2791" />
        <div className="text-sm text-blue-900" data-id="element-2792">
          <strong data-id="element-2793">Role-Based Access:</strong> Users will only see features and
          data relevant to their assigned role and station.
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-2794">
        <div className="flex gap-4" data-id="element-2795">
          <button onClick={() => setActiveTab('list')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'list' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2796">
            User List
          </button>
          <button onClick={() => setActiveTab('add')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'add' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-2797">
            Add User
          </button>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'list' ? <DataTable columns={columns} data={users} filters={filters} onView={row => console.log('View', row)} onEdit={row => console.log('Edit', row)} searchPlaceholder="Search users..." data-id="element-2798" /> : <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      if (!formData.fullName || !formData.email || !formData.role) {
        alert('Please fill in all required fields.');
        return;
      }
      alert(`User ${formData.fullName} created successfully!`);
      setCurrentStep(0);
      setActiveTab('list');
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        nid: '',
        role: '',
        station: '',
        permissions: {
          approveBeneficiaries: false,
          issuePermits: false,
          manageVessels: false,
          fileCases: false
        }
      });
    }} data-id="element-2799">
          {currentStep === 0 && <div className="space-y-4" data-id="element-2800">
              <div className="grid grid-cols-2 gap-4" data-id="element-2801">
                <div data-id="element-2802">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2803">
                    Full Name *
                  </label>
                  <input type="text" required value={formData.fullName} onChange={e => setFormData({
              ...formData,
              fullName: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2804" />
                </div>
                <div data-id="element-2805">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2806">
                    Email *
                  </label>
                  <input type="email" required value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2807" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-2808">
                <div data-id="element-2809">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2810">
                    Phone Number *
                  </label>
                  <input type="tel" required value={formData.phone} onChange={e => setFormData({
              ...formData,
              phone: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2811" />
                </div>
                <div data-id="element-2812">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2813">
                    NID Number *
                  </label>
                  <input type="text" required value={formData.nid} onChange={e => setFormData({
              ...formData,
              nid: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2814" />
                </div>
              </div>
            </div>}
          {currentStep === 1 && <div className="space-y-4" data-id="element-2815">
              <div data-id="element-2816">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2817">
                  Role *
                </label>
                <select required value={formData.role} onChange={e => setFormData({
            ...formData,
            role: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2818">
                  <option value="" data-id="element-2819">Select Role</option>
                  <option value="hq" data-id="element-2820">HQ Admin</option>
                  <option value="range" data-id="element-2821">Range Officer</option>
                  <option value="station" data-id="element-2822">Station Officer</option>
                  <option value="field" data-id="element-2823">Field Officer</option>
                </select>
              </div>
              <div data-id="element-2824">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2825">
                  Assigned Station/Range *
                </label>
                <select required value={formData.station} onChange={e => setFormData({
            ...formData,
            station: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-2826">
                  <option value="" data-id="element-2827">Select Station/Range</option>
                  <option value="chandpai" data-id="element-2828">Chandpai Range</option>
                  <option value="katka" data-id="element-2829">Katka Station</option>
                  <option value="dhangmari" data-id="element-2830">Dhangmari Station</option>
                  <option value="harbaria" data-id="element-2831">Harbaria Station</option>
                </select>
              </div>
              <div data-id="element-2832">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2833">
                  Permissions
                </label>
                <div className="space-y-2" data-id="element-2834">
                  <label className="flex items-center gap-2" data-id="element-2835">
                    <input type="checkbox" checked={formData.permissions.approveBeneficiaries} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  approveBeneficiaries: e.target.checked
                }
              })} className="w-4 h-4 text-[#1B5E20] rounded focus:ring-[#1B5E20]" data-id="element-2836" />
                    <span className="text-sm text-gray-700" data-id="element-2837">
                      Approve Beneficiaries
                    </span>
                  </label>
                  <label className="flex items-center gap-2" data-id="element-2838">
                    <input type="checkbox" checked={formData.permissions.issuePermits} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  issuePermits: e.target.checked
                }
              })} className="w-4 h-4 text-[#1B5E20] rounded focus:ring-[#1B5E20]" data-id="element-2839" />
                    <span className="text-sm text-gray-700" data-id="element-2840">Issue Permits</span>
                  </label>
                  <label className="flex items-center gap-2" data-id="element-2841">
                    <input type="checkbox" checked={formData.permissions.manageVessels} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  manageVessels: e.target.checked
                }
              })} className="w-4 h-4 text-[#1B5E20] rounded focus:ring-[#1B5E20]" data-id="element-2842" />
                    <span className="text-sm text-gray-700" data-id="element-2843">
                      Manage Vessels
                    </span>
                  </label>
                  <label className="flex items-center gap-2" data-id="element-2844">
                    <input type="checkbox" checked={formData.permissions.fileCases} onChange={e => setFormData({
                ...formData,
                permissions: {
                  ...formData.permissions,
                  fileCases: e.target.checked
                }
              })} className="w-4 h-4 text-[#1B5E20] rounded focus:ring-[#1B5E20]" data-id="element-2845" />
                    <span className="text-sm text-gray-700" data-id="element-2846">File Cases</span>
                  </label>
                </div>
              </div>
            </div>}
          {currentStep === 2 && <div className="space-y-6" data-id="element-2847">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-2848">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-2849">
                  Basic Information
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2850">
                  <div data-id="element-2851">
                    <span className="text-gray-600" data-id="element-2852">Name:</span>{' '}
                    <span className="font-medium" data-id="element-2853">
                      {formData.fullName || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2854">
                    <span className="text-gray-600" data-id="element-2855">Email:</span>{' '}
                    <span className="font-medium" data-id="element-2856">
                      {formData.email || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2857">
                    <span className="text-gray-600" data-id="element-2858">Phone:</span>{' '}
                    <span className="font-medium" data-id="element-2859">
                      {formData.phone || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-2860">
                    <span className="text-gray-600" data-id="element-2861">NID:</span>{' '}
                    <span className="font-medium" data-id="element-2862">
                      {formData.nid || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-2863">
                <h4 className="font-semibold text-gray-900 mb-3" data-id="element-2864">
                  Role & Permissions
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-2865">
                  <div data-id="element-2866">
                    <span className="text-gray-600" data-id="element-2867">Role:</span>{' '}
                    <span className="font-medium capitalize" data-id="element-2868">
                      {formData.role || 'Not selected'}
                    </span>
                  </div>
                  <div data-id="element-2869">
                    <span className="text-gray-600" data-id="element-2870">Station:</span>{' '}
                    <span className="font-medium capitalize" data-id="element-2871">
                      {formData.station || 'Not selected'}
                    </span>
                  </div>
                  <div className="col-span-2" data-id="element-2872">
                    <span className="text-gray-600" data-id="element-2873">Permissions:</span>{' '}
                    <span className="font-medium" data-id="element-2874">
                      {Object.entries(formData.permissions).filter(([_, val]) => val).map(([key]) => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())).join(', ') || 'None selected'}
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </WizardForm>}
    </div>;
}